"use client"

import { useState, useEffect } from "react"
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom"
import { Award, CheckCircle, ChevronDown, FileText, Home, Menu, Shield, Upload, X } from "lucide-react"
import { useAuth } from "../../context/AuthContext"
import "./NavigationBar.css"

export default function NavigationBar() {
  const location = useLocation()
  const navigate = useNavigate()
  const { isAuthenticated, user, logout } = useAuth()

  const [isOpen, setIsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      const scrollThreshold = window.innerWidth < 768 ? 5 : 10
      setScrolled(window.scrollY > scrollThreshold)
    }

    window.addEventListener("scroll", handleScroll)
    window.addEventListener("touchmove", handleScroll)
    handleScroll()

    return () => {
      window.removeEventListener("scroll", handleScroll)
      window.removeEventListener("touchmove", handleScroll)
    }
  }, [])

  const handleLogout = () => {
    logout()
    navigate("/login")
    setIsOpen(false)
  }

  const isAdminPathActive = () => {
    return ["/admin", "/admin/templates", "/admin/certificate-events", "/admin/upload", "/admin/certificates"].some(
      (path) => location.pathname.startsWith(path),
    )
  }

  const navigationItems = [
    { to: "/", label: "Inicio", icon: Home, exact: true },
    { to: "/student/certificates", label: "Mis Certificados", icon: FileText },
    { to: "/verify", label: "Verificar", icon: CheckCircle },
  ]

  const adminItems = [
    { to: "/admin", label: "Panel Admin", icon: Shield },
    { to: "/admin/templates", label: "Plantillas", icon: FileText },
    { to: "/admin/certificate-events", label: "Eventos", icon: Award },
    { to: "/admin/upload", label: "Cargar Certificados", icon: Upload },
    { to: "/admin/certificates", label: "Listar Certificados", icon: FileText },
  ]

  return (
    <nav className={`modern-navbar ${scrolled ? "scrolled" : ""}`}>
      <div className="navbar-wrapper">
        {/* Logo Section */}
        <Link to="/" className="logo-section">
          <div className="logo-container">
            <Award className="logo-icon" />
            <span className="logo-text">EduCertify</span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <div className="nav-section">
          <div className="nav-items">
            {navigationItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              >
                <item.icon className="nav-item-icon" />
                <span className="nav-item-text">{item.label}</span>
                <div className="nav-item-indicator"></div>
              </NavLink>
            ))}

            {/* Admin Dropdown */}
            {isAuthenticated && user && user.role === "admin" && (
              <div
                className={`nav-dropdown ${isAdminPathActive() ? "active" : ""}`}
                onMouseEnter={() => setDropdownOpen(true)}
                onMouseLeave={() => setDropdownOpen(false)}
              >
                <button className="nav-dropdown-trigger">
                  <Shield className="nav-item-icon" />
                  <span className="nav-item-text">Admin</span>
                  <ChevronDown className={`dropdown-arrow ${dropdownOpen ? "open" : ""}`} />
                  <div className="nav-item-indicator"></div>
                </button>

                <div className={`dropdown-menu ${dropdownOpen ? "open" : ""}`}>
                  <div className="dropdown-content">
                    {adminItems.map((item) => (
                      <Link key={item.to} to={item.to} className="dropdown-item">
                        <item.icon className="dropdown-item-icon" />
                        <span className="dropdown-item-text">{item.label}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Auth Section */}
        <div className="auth-section">
          {isAuthenticated ? (
            <button onClick={handleLogout} className="auth-button logout">
              <span className="auth-text">Cerrar Sesión</span>
              <span className="user-name">({user?.username})</span>
            </button>
          ) : (
            <NavLink to="/login" className={({ isActive }) => `auth-button login ${isActive ? "active" : ""}`}>
              <span className="auth-text">Iniciar Sesión</span>
              <div className="nav-item-indicator"></div>
            </NavLink>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button className="mobile-toggle" onClick={() => setIsOpen(true)}>
          <Menu className="mobile-toggle-icon" />
        </button>

        {/* Mobile Menu */}
        {isOpen && (
          <>
            <div className="mobile-overlay" onClick={() => setIsOpen(false)}></div>
            <div className="mobile-menu">
              <div className="mobile-header">
                <div className="mobile-logo">
                  <Award className="logo-icon" />
                  <span className="logo-text">EduCertify</span>
                </div>
                <button className="mobile-close" onClick={() => setIsOpen(false)}>
                  <X className="close-icon" />
                </button>
              </div>

              <div className="mobile-nav">
                <div className="mobile-section">
                  {navigationItems.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.exact}
                      onClick={() => setIsOpen(false)}
                      className={({ isActive }) => `mobile-nav-item ${isActive ? "active" : ""}`}
                    >
                      <item.icon className="mobile-nav-icon" />
                      <span className="mobile-nav-text">{item.label}</span>
                    </NavLink>
                  ))}
                </div>

                {isAuthenticated && user && user.role === "admin" && (
                  <div className="mobile-section">
                    <div className="mobile-section-title">Administración</div>
                    {adminItems.map((item) => (
                      <Link key={item.to} to={item.to} onClick={() => setIsOpen(false)} className="mobile-nav-item">
                        <item.icon className="mobile-nav-icon" />
                        <span className="mobile-nav-text">{item.label}</span>
                      </Link>
                    ))}
                  </div>
                )}

                <div className="mobile-auth">
                  {isAuthenticated ? (
                    <button onClick={handleLogout} className="mobile-auth-button">
                      <span>Cerrar Sesión ({user?.username})</span>
                    </button>
                  ) : (
                    <NavLink to="/login" onClick={() => setIsOpen(false)} className="mobile-auth-button">
                      <span>Iniciar Sesión</span>
                    </NavLink>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </nav>
  )
}
